import React, { useCallback, useMemo } from "react";
import { AHSeparator, AHText, Box } from "arkh";
import { graphql, useFragment } from "react-relay";
import { WorkspaceRoutineOccasionStoresList_routineWorkspaceOccasion$key } from "./__generated__/WorkspaceRoutineOccasionStoresList_routineWorkspaceOccasion.graphql";
import { FlatList } from "react-native";
import { useOrgListStyleProps } from "../../useOrgHomeDetailContainerStyle";
import { WorkspaceRoutineOccasionDescription } from "./WorkspaceRoutineOccasionDescription";
import {
  WorkspaceRoutineOccasionListHeader,
  WorkspaceRoutineOccasionOrderByKey,
} from "./WorkspaceRoutineOccasionListHeader";
import { WorkspaceRoutineOccasionStoresListItem } from "./WorkspaceRoutineOccasionStoresListItem";
import { FormattedMessage } from "react-intl";
import { workspaceRoutineOccasionSort } from "./workspaceRoutineOccasionSort";
import { WorkspaceEmptyListItem } from "../../WorkspaceEmptyListItem";

export function WorkspaceRoutineOccasionStoresList(props: {
  occasion: WorkspaceRoutineOccasionStoresList_routineWorkspaceOccasion$key;
  onPress(
    routineId: string,
    storeId: string,
    storeName: string,
    storeTimezone: string,
    sequenceNumber: number
  ): void;
  openOccasionsHistory: () => void;
}) {
  const [search, setSearch] = React.useState("");
  const [order, setOrder] = React.useState<"ASC" | "DESC">("DESC");
  const [orderBy, setOrderBy] =
    React.useState<WorkspaceRoutineOccasionOrderByKey>("status");
  const data = useFragment(
    graphql`
      fragment WorkspaceRoutineOccasionStoresList_routineWorkspaceOccasion on RoutineWorkspaceOccasion {
        ...WorkspaceRoutineOccasionListHeader_RoutineWorkspaceOccasion
        routine {
          id
          ...WorkspaceRoutineOccasionDescription_routine
        }
        totalTaskExecutionCount

        occasions {
          store {
            id
            name
          }
          occasion {
            id
            routine {
              id
            }

            occasionStatus: status
            issueCount
            registeredIssueCount
          }
          ...WorkspaceRoutineOccasionStoresListItem_routineWorkspaceOccasion
        }
      }
    `,
    props.occasion
  );

  const filteredOccasions = useMemo(() => {
    return search.length > 0
      ? data.occasions.filter((item) =>
          item.store.name
            .toLocaleLowerCase()
            .includes(search.toLocaleLowerCase())
        )
      : data.occasions.slice();
  }, [data.occasions, search]);

  const listData = useMemo(() => {
    const sortedOcccasions = workspaceRoutineOccasionSort(
      filteredOccasions,
      orderBy,
      order
    ).map((occasion) => ({ type: "occasions", occasion }) as const);
    return [
      {
        type: "header" as const,
      },
      ...sortedOcccasions,
      ...(sortedOcccasions.length === 0 ? [{ type: "empty" as const }] : []),
    ] as const;
  }, [filteredOccasions, order, orderBy]);

  const onPressListItem = useCallback(
    (
      storeId: string,
      storeName: string,
      storeTimezone: string,
      sequenceNumber: number
    ) => {
      props.onPress(
        data.routine.id,
        storeId,
        storeName,
        storeTimezone,
        sequenceNumber
      );
    },
    [data.routine.id, props]
  );
  const renderItem = useCallback(
    ({ item, index }: { item: (typeof listData)[number]; index: number }) => {
      if (item.type === "empty") {
        return (
          <WorkspaceEmptyListItem
            type={search === "" ? "EMPTY" : "EMPTY_SEARCH"}
          />
        );
      }
      if (item.type === "header") {
        return (
          <WorkspaceRoutineOccasionListHeader
            openOccasionsHistory={props.openOccasionsHistory}
            search={search}
            setSearch={setSearch}
            order={order}
            setOrder={setOrder}
            orderBy={orderBy}
            setOrderBy={setOrderBy}
            workspaceOccasion={data}
            isLast={listData.length - 1 === index}
          />
        );
      }
      return (
        <WorkspaceRoutineOccasionStoresListItem
          occasion={item.occasion}
          isLast={listData.length - 1 === index}
          onPress={onPressListItem}
        />
      );
    },
    [
      data,
      listData.length,
      onPressListItem,
      order,
      orderBy,
      props.openOccasionsHistory,
      search,
    ]
  );

  const ListHeaderComponent = useMemo(() => {
    return (
      <Box spaceTop="medium">
        {(data.totalTaskExecutionCount ?? 0) > 0 ? (
          <AHText
            spaceBottom="medium"
            type="subtitle"
            color="text-secondary"
            spaceHorizontal="medium"
          >
            <FormattedMessage
              defaultMessage="Checklist: {totalTaskExecutionCount, plural, =1 {# task} other {# tasks}}"
              description="WorkspaceRoutineOccasionStoresList totalTaskExecutionCount label"
              values={{
                totalTaskExecutionCount: data.totalTaskExecutionCount,
              }}
            />
          </AHText>
        ) : null}
        <WorkspaceRoutineOccasionDescription routine={data.routine} />
      </Box>
    );
  }, [data.routine, data.totalTaskExecutionCount]);

  const listProps = useOrgListStyleProps();

  return (
    <FlatList
      {...listProps}
      ItemSeparatorComponent={AHSeparator}
      data={listData}
      ListHeaderComponent={ListHeaderComponent}
      ListEmptyComponent={
        <WorkspaceEmptyListItem
          type={search === "" ? "EMPTY" : "EMPTY_SEARCH"}
        />
      }
      stickyHeaderIndices={[1]}
      renderItem={renderItem}
    />
  );
}
